import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

import "../Css/Profile.css";
import API_BASE_URL, { API_IMAGE_BASE_URL } from "../../api/config";
import COLLEGE_MAP from "../../constants/colleges";
// import Navbar from "../Navbar";

const ProfileEdit = () => {
  // const [uploadedImagePath, setUploadedImagePath] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [image, setImage] = useState("");
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [id, setId] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [loading, setLoading] = useState(false); // Added loading state
  const [fetchError, setFetchError] = useState(null); // Added fetch error state

  // Profile Information States
  // const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [dob, setDoB] = useState("");
  const [dobError, setDobError] = useState("");
  const [gender, setGender] = useState("");
  // const [mentorName, setMentorName] = useState("");
  // const [mentorOptions, setMentorOptions] = useState([]);

  // Contact Information States
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [socialLink, setSocialLink] = useState("");
  const [address, setAddress] = useState("");

  // About User
  const [bio, setBio] = useState("");

  // About College
  const [collegeName, setCollegeName] = useState("");
  const [department, setDepartment] = useState("");
  const [year, setYear] = useState("");
  const [skills, setSkills] = useState("");
  const [hobbies, setHobbie] = useState("");
  const [strength, setStrength] = useState("");
  const [weakness, setWeakness] = useState("");
  const [achievements, setAchievements] = useState("");
  const [Inspiration, setInspiration] = useState("");

  useEffect(() => {
    setFadeIn(true);
    setLoading(true); // Start loading

    const storedEmail = Cookies.get("email");
    const storedUsername = Cookies.get("username");
    // fetchMentorList();

    if (storedEmail && storedUsername) {
      setEmail(storedEmail);
      // setUsername(storedUsername);
      fetchUserProfile(storedEmail);
    } else {
      Swal.fire({
        icon: "warning",
        title: "Authentication Error",
        text: "No user data found. Please log in again.",
      }).then(() => {
        window.location.href = "/Login"; // Redirect to login page after alert
      });
      setLoading(false); // Stop loading if no user data
    }
  }, []);

  const fetchUserProfile = async (userEmail) => {
    try {
      const safeEmail = encodeURIComponent(userEmail);
      const response = await fetch(
        `${API_BASE_URL}/api/profile/read_byEmail?email=${safeEmail}`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        console.error(
          "Error fetching profile by read_by_email:",
          response.statusText,
        );
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const raw = await response.json();
      const data = raw.data ?? null; // unwrap the { success, message, data } envelope

      if (data && !data.error) {
        setId(data.id);
        setFullName(data.full_name || "");
        setBloodGroup(data.blood_group || "");
        setDoB(data.dob || "");
        setGender(data.gender || "");
        setWhatsapp(data.phone_number || "");
        setSocialLink(data.social_links || "");
        setAddress(data.address || "");
        setBio(data.bio || "");
        setCollegeName(data.college || "");
        setDepartment(data.department || "");
        setYear(data.year_of_study || "");
        setSkills(data.skills || "");
        setHobbie(data.hobbies || "");
        setStrength(data.strength || "");
        setWeakness(data.weakness || "");
        setAchievements(data.achievements || "");
        setInspiration(data.inspiration_source || "");
        setImage(
          data.profile_image
            ? `${API_IMAGE_BASE_URL}/${data.profile_image}`
            : "",
        );
      } else if (data === null) {
        // New user: no profile row yet — form stays empty, ready to fill in
        console.info("No profile found yet — form ready for first-time setup.");
      } else if (data && data.error) {
        console.error("Error fetching profile:", data.error);
        Swal.fire({
          icon: "error",
          title: "Error Fetching Profile",
          text: data.error,
        });
        setFetchError(data.error);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      Swal.fire({
        icon: "error",
        title: "Please Log in again",
        text: `Failed to fetch profile data: ${error.message}`,
      });
      setFetchError(error.message);

      // Optional but recommended: Kick them back to login if the fetch totally fails
      // setTimeout(() => { navigate('/login'); }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData2 = new FormData();
    formData2.append("profile_image", file);
    formData2.append("id", id);

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/profile/update_images`,
        {
          method: "POST",
          body: formData2,
          credentials: "include",
        },
      );

      const text = await response.text();

      if (response.ok && text) {
        fetchUserProfile(email); // Refresh profile data after upload
        Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          text: "Profile Pic Updated Successfully.",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: text.error || "Failed to upload image.",
        });
      }
    } catch (error) {
      console.error("Upload Error:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Error",
        text:
          error.message || "Unexpected error occurred while uploading image.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6 || newPassword.length > 12) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Password",
        text: "Password length must be in between 6 to 12.",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("new_password", newPassword);

      const response = await fetch(
        `${API_BASE_URL}/api/forget_password/change_password`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        },
      );

      const resultText = await response.text();
      try {
        JSON.parse(resultText);
      } catch (e) {
        throw new Error(`Invalid response: ${resultText}`);
      }

      if (response.ok) {
        setPasswordChanged(true);
        setNewPassword("");
        Swal.fire({
          icon: "success",
          title: "Password Updated",
          text: "Your password has been updated successfully!",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error Changing Password",
          text: "Failed to update password",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    }
  };

  // const fetchMentorList = async () => {
  //   try {
  //     const response = await fetch("https://ethiccraft.org/backend/Facilitator_Panel/read.php", {
  //       credentials: 'include'
  //     });

  //     if (!response.ok) throw new Error("Failed to fetch mentor list");
  //     const data = await response.json();

  //     // Ensure `data` is an array of objects with at least a `name` or `full_name`
  //     if (Array.isArray(data)) {
  //       setMentorOptions(data);
  //     } else {
  //       throw new Error("Invalid mentor data format");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching mentors:", error);
  //     Swal.fire({
  //       icon: "error",
  //       title: "Mentor List Error",
  //       text: error.message,
  //     });
  //   }
  // };

  const handleDOBChange = (e) => {
    const dobValue = e.target.value;
    setDoB(dobValue);

    const today = new Date();
    const selectedDate = new Date(dobValue);

    let error = "";

    if (selectedDate >= today) {
      error = "Date of Birth cannot be today or in the future";
    } else {
      const age =
        today.getFullYear() -
        selectedDate.getFullYear() -
        (today <
        new Date(
          today.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
        )
          ? 1
          : 0);

      if (age < 12) {
        error = "Age must be at least 12 years";
      }
    }

    setDobError(error);
  };

  const handleWhatsappChange = (e) => {
    const phoneValue = e.target.value;
    const phoneRegex = /^\d{10}$/;
    setWhatsapp(phoneValue);
    setPhoneError(phoneRegex.test(phoneValue) ? "" : "Invalid phone number");
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the default form submission
    setLoading(true); // Start loading

    const requiredFields = [
      { label: "Full Name", value: fullName },
      { label: "Email", value: email },
      { label: "Phone Number", value: whatsapp },
      { label: "Gender", value: gender },
      { label: "Date of Birth", value: dob },
      { label: "Blood Group", value: bloodGroup },
      { label: "College Name", value: collegeName },
      { label: "Department", value: department },
      { label: "Year of Study", value: year },
      { label: "Skills", value: skills },
      // { label: "Strength", value: strength },
      { label: "Address", value: address },
      // { label: "Achievements", value: achievements },
      // { label: "Weakness", value: weakness },
      // { label: "Mentor Name", value: mentorName },
    ];

    const emptyField = requiredFields.find((field) => {
      return String(field.value || "").trim() === "";
    });

    if (emptyField) {
      setLoading(false);
      Swal.fire({
        icon: "warning",
        title: "Missing Field",
        text: `Please fill in the "${emptyField.label}" field before saving.`,
      });
      return;
    }

    const formData = new FormData();

    formData.append("full_name", fullName);
    formData.append("phone_number", whatsapp);
    formData.append("gender", gender);
    formData.append("dob", dob);
    formData.append("blood_group", bloodGroup);
    formData.append("bio", bio);
    formData.append("college", collegeName);
    formData.append("department", department);
    formData.append("year_of_study", year);
    formData.append("skills", skills);
    formData.append("address", address);
    formData.append("social_links", socialLink);
    formData.append("hobbies", hobbies);
    formData.append("strength", strength);
    formData.append("weakness", weakness);
    formData.append("achievements", achievements);
    formData.append("inspiration_source", Inspiration);
    // formData.append("Mentors_Name", mentorName);
    formData.append("isShown", 1);

    // Validate email and phone before submitting
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;
    let valid = true;

    if (!emailRegex.test(email)) {
      // setEmailError("Invalid email format");
      valid = true;
    } else {
      setEmailError("");
    }

    if (whatsapp && !phoneRegex.test(whatsapp)) {
      setPhoneError("Invalid phone number");
      valid = false;
    } else {
      setPhoneError("");
    }

    if (!valid) {
      setLoading(false); // Stop loading on validation error
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/profile/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const text = await response.text();
      let json = {};
      try {
        json = JSON.parse(text);
      } catch (e) {
        throw new Error(`Response was not valid JSON: ${text}`);
      }

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: json.message || "Profile updated successfully!",
        }).then(() => {
          // Use full page reload so ProfileCard re-fetches fresh data
          window.location.href = "/dashboard";
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: json.error || "Failed to update profile.",
        });
      }
    } catch (error) {
      console.error("POST ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.message || "An unexpected error occurred.",
      });
    } finally {
      setLoading(false); // Stop loading after API call
    }
  };

  if (loading) {
    return (
      <div className="profile-edit-container fade-in">
        <div className="loading-indicator">Loading...</div>{" "}
        {/* Simple loading indicator */}
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="profile-edit-container fade-in">
        <div className="error-message">Error: {fetchError}</div>
      </div>
    );
  }

  const departmentOptions = [
    "Computer Engineering",
    "Information Technology",
    "Computer Science(Data-Science)",
    "Computer Engineering(Hindi)",
    "Electronics and Communication",
    "Mechanical Engineering",
    "Robotics and Artificial Intelligence",
    "Mechanical Engineering(Hindi)",
    "Civil Engineering",
    "Chemical Engineering",
    "Biotechnology",
    "Electrical Engineering",
    "B.Sc Mathematics",
    "B.Sc Physics",
    "B.Sc Chemistry",
    "M.Sc Mathematics",
    "M.Sc Physics",
    "M.Sc Chemistry",
    "Bachelors of Business Administration",
    "Masters of Business Administration",
    "Other",
  ];
  const Skills = [
    "Web-Development",
    "Designing",
    "Event_Management",
    "Video_Editing",
    "Image_Editing",
    "Photography",
    "Videography",
    "Content_Writing",
    "Anchoring/Hosting",
    "Debate",
    "Social_Media_Management",
    "Presentation_Skills",
    "Volunteering",
    "Acting/Theatre",
    "Dance",
    "Instrument Playing",
    "Singing",
    "Critical Thinking",
    "Creativity",
    "Other",
  ];

  return (
    <div className={`profile-edit-container ${fadeIn ? "fade-in" : ""}`}>
      <aside className="profile_edit-sidebar">
        <div className="profile-image-container">
          <img src={image} alt="Profile" className="profile-image" />
        </div>
        <div className="upload-section">
          <input
            type="file"
            id="upload"
            accept="image/*"
            onChange={handleImageUpload}
            hidden
          />
          <label htmlFor="upload" className="upload-image-label">
            Upload Photo
          </label>
        </div>

        <div className="password-change-section">
          <h3>Change Password</h3>
          {/* <div className="password-inputs">
            <label htmlFor="old-password">Old Password</label>
            <input type="password" id="old-password" placeholder="Old Password" />
          </div> */}
          <div className="password-inputs">
            <label htmlFor="new-password">New Password</label>
            <input
              type="password"
              id="new-password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <button
            className="change-password-button"
            onClick={handleChangePassword}
          >
            Change Password
          </button>
          {passwordChanged && (
            <p className="success-message">Password changed successfully!</p>
          )}
        </div>
      </aside>

      <main className="profile_edit-content">
        <form onSubmit={handleSubmit}>
          <section className="profile-information-section">
            <h2>Profile Information</h2>
            <div className="form-grid">
              {/* <div className="form-group">
                <label htmlFor="username">Username</label>
                <input type="text" id="username" value={username} readOnly />
              </div> */}
              <div className="form-group">
                <label htmlFor="full-name">Full Name</label>
                <input
                  type="text"
                  id="full-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="blood-group">Blood Group</label>
                <select
                  id="blood-group"
                  value={bloodGroup}
                  onChange={(e) => setBloodGroup(e.target.value)}
                >
                  <option value="" disabled>
                    Select Blood Group
                  </option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="dob">Date Of Birth</label>
                <input
                  type="date"
                  id="dob"
                  value={dob}
                  onChange={handleDOBChange}
                />
                {dobError && <p className="error-message">{dobError}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="gender">Gender</label>
                <select
                  id="gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="" disabled>
                    Select Gender
                  </option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {/* <div className="form-group">
                <label htmlFor="Mentors_Name">Mentor Name</label>
                <select id="Mentors_Name" value={mentorName} onChange={(e) => setMentorName(e.target.value)}>
                  <option value="">Select Mentor</option>
                  {mentorOptions.map((mentor, index) => (
                    <option key={index} value={mentor.name || mentor.Full_Name}>
                      {mentor.name || mentor.Full_Name}
                    </option>
                  ))}
                </select>
              </div> */}
            </div>
          </section>

          <section className="contact-information-section">
            <h2>Contact Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="email">Email (required)</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  readOnly
                  required
                />
                {emailError && <p className="error-message">{emailError}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="whatsapp">WhatsApp Number</label>
                <input
                  type="text"
                  id="whatsapp"
                  value={whatsapp}
                  onChange={handleWhatsappChange}
                />
                {phoneError && <p className="error-message">{phoneError}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="social-link">Instagram (ex, @your_id)</label>
                <input
                  type="text"
                  id="social-link"
                  value={socialLink}
                  onChange={(e) => setSocialLink(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="about-user-section">
            <h2>About the User</h2>
            <div className="form-group">
              <label htmlFor="bio">Biographical Info</label>
              <textarea
                id="bio"
                rows="5"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
              />
            </div>
          </section>

          <section className="study-information-section">
            <h2>Study Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="college-name">University/College Name</label>
                <select
                  id="college-name"
                  value={collegeName}
                  onChange={(e) => setCollegeName(e.target.value)}
                >
                  <option value="" disabled>
                    -- Select College --
                  </option>
                  {Object.entries(COLLEGE_MAP).map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="department">Department</label>
                <select
                  id="department"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                >
                  <option value="" disabled>
                    Select Department
                  </option>
                  {departmentOptions.map((dept, index) => (
                    <option key={index} value={dept}>
                      {" "}
                      {dept}{" "}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="year">Graduation Year</label>
                <input
                  type="number"
                  id="year"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  min="2000"
                  max="2050"
                  step="1"
                  placeholder="YYYY"
                />
              </div>
              <div className="form-group">
                <label htmlFor="skills">Skills</label>
                <select
                  id="skills"
                  value={skills}
                  onChange={(e) => setSkills(e.target.value)}
                >
                  <option value="" disabled>
                    Select Skills
                  </option>
                  {Skills.map((skill, index) => (
                    <option key={index} value={skill}>
                      {" "}
                      {skill}{" "}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="hobbies">Hobbies</label>
                <input
                  type="text"
                  id="hobbies"
                  value={hobbies}
                  onChange={(e) => setHobbie(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="strength">Strength</label>
                <input
                  type="text"
                  id="strength"
                  value={strength}
                  onChange={(e) => setStrength(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="weakness">Weakness</label>
                <input
                  type="text"
                  id="weakness"
                  value={weakness}
                  onChange={(e) => setWeakness(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="achievements">
                  Achievements(eg Certificates)
                </label>
                <input
                  type="text"
                  id="achievements"
                  value={achievements}
                  onChange={(e) => setAchievements(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="Inspiration">Source Of Inspiration</label>
                <input
                  type="text"
                  id="Inspiration"
                  value={Inspiration}
                  onChange={(e) => setInspiration(e.target.value)}
                />
              </div>
            </div>
          </section>

          <button className="submit-button" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save & Continue"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default ProfileEdit;
